import React, { FC, useState } from 'react';
import { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslation';
import Title, { TitleTagTypes } from '@ui/SectionTitle/Title';
import { publicAssets } from '@utils/url.utils';
import ContentTemplate from '../../ui/Content/Content';
import {
    Company,
    CompanyLabel, CompanyLink,
    Contact,
    ContactMap,
    Map,
    Transportation,
    TransportationDescription,
    TransportationHeader,
    TransportationHeaderIcon,
    TransportationHeaderTitle,
    TransportationOption,
    TransportationOptions,
} from './Address.elements';
import useScrollToSection from "@hooks/useScrollToSection";

enum TRANSPORTATION_OPTIONS {
    TRAM = 'tram',
    METRO = 'metro',
    BUS = 'bus',
    TRAIN = 'train',
    CAR = 'car',
}

export interface AddressProps {}

const AddressTemplate: FC<AddressProps> = () => {
    const { t } = useTranslations();

    const [openedTransportationOptions, setOpenedTransportationOptions] = useState([]);

    useScrollToSection('kde-nas-najdete', 'kdenasnajdete');

    const toggleTransportationOption = (option: TRANSPORTATION_OPTIONS) => {
        openedTransportationOptions.includes(option)
            ? setOpenedTransportationOptions([])
            : setOpenedTransportationOptions([option]);
    };

    return (
        <ContentTemplate withPadding mobileSidePadding={20} id="kdenasnajdete">
            <Title tagType={TitleTagTypes.H2} isBlack>
                {t('contact.company.title')}
            </Title>
            <Contact>
                <Company>
                    <CompanyLabel isBold>Učebny Studio W</CompanyLabel>
                    <CompanyLabel marginTop={10}>Karlovo náměstí 3</CompanyLabel>
                    <CompanyLabel>120 00 Praha 2</CompanyLabel>
                    <CompanyLink target="_blank" href="https://maps.app.goo.gl/w4en6jh3rwZjWEy37">Zobrazit na mapě</CompanyLink>
                    <CompanyLabel marginTop={10}>Novotného lávka 5</CompanyLabel>
                    <CompanyLabel>110 00 Praha 1</CompanyLabel>
                    <CompanyLink target="_blank" href="https://maps.app.goo.gl/qNG83N7QKmQknuFF6">Zobrazit na mapě</CompanyLink>
                </Company>
                <Company>
                    <CompanyLabel isBold>
                        Studio W kancelář
                    </CompanyLabel>
                    <CompanyLabel marginTop={10}>Novotného lávka 5</CompanyLabel>
                    <CompanyLabel>110 00 Praha 1</CompanyLabel>
                    <CompanyLink target="_blank" href="https://maps.app.goo.gl/qNG83N7QKmQknuFF6">Zobrazit na mapě</CompanyLink>
                </Company>
                <Company>
                    <CompanyLabel isBold>
                        Fakturační údaje
                    </CompanyLabel>
                    <CompanyLabel marginTop={10}>Poradce Podnikatele, spol. s r.o.</CompanyLabel>
                    <CompanyLabel>Jablonského 639/4</CompanyLabel>
                    <CompanyLabel>170 00 Praha 7</CompanyLabel>
                    <CompanyLabel marginTop={10}>IČO: 49678876</CompanyLabel>
                    <CompanyLabel>DIČ: CZ49678876</CompanyLabel>
                    <CompanyLabel>Č. účtu: 151361399/0600</CompanyLabel>
                    <CompanyLabel>IBAN: CZ7206000000000151361399</CompanyLabel>
                    <CompanyLabel>MONETA Money Bank, a.s.</CompanyLabel>
                </Company>
            </Contact>
            <Transportation>
                <Title tagType={TitleTagTypes.H3} alignLeft isBlack>
                    <span dangerouslySetInnerHTML={{ __html: t('contact.transportation.title') }} />
                </Title>
                <TransportationOptions>
                    <TransportationOption>
                        <TransportationHeader onClick={() => toggleTransportationOption(TRANSPORTATION_OPTIONS.TRAM)}>
                            <TransportationHeaderTitle>
                                {t('contact.transportation.tram.label')}
                            </TransportationHeaderTitle>
                            <TransportationHeaderIcon
                                className={
                                    openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.TRAM)
                                        ? 'isOpen'
                                        : 'isClose'
                                }
                                name={Icons.ArrowDown}
                            />
                        </TransportationHeader>
                        {openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.TRAM) && (
                            <TransportationDescription>
                                <p dangerouslySetInnerHTML={{ __html: t('contact.transportation.tram.description') }} />
                            </TransportationDescription>
                        )}
                    </TransportationOption>
                    <TransportationOption>
                        <TransportationHeader onClick={() => toggleTransportationOption(TRANSPORTATION_OPTIONS.METRO)}>
                            <TransportationHeaderTitle>
                                {t('contact.transportation.metro.label')}
                            </TransportationHeaderTitle>
                            <TransportationHeaderIcon
                                className={
                                    openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.METRO)
                                        ? 'isOpen'
                                        : 'isClose'
                                }
                                name={Icons.ArrowDown}
                            />
                        </TransportationHeader>
                        {openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.METRO) && (
                            <TransportationDescription>
                                <p dangerouslySetInnerHTML={{ __html: t('contact.transportation.metro.description') }} />
                            </TransportationDescription>
                        )}
                    </TransportationOption>
                    <TransportationOption>
                        <TransportationHeader onClick={() => toggleTransportationOption(TRANSPORTATION_OPTIONS.TRAIN)}>
                            <TransportationHeaderTitle>
                                {t('contact.transportation.train.label')}
                            </TransportationHeaderTitle>
                            <TransportationHeaderIcon
                                className={
                                    openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.TRAIN)
                                        ? 'isOpen'
                                        : 'isClose'
                                }
                                name={Icons.ArrowDown}
                            />
                        </TransportationHeader>
                        {openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.TRAIN) && (
                            <TransportationDescription>
                                <p
                                    dangerouslySetInnerHTML={{ __html: t('contact.transportation.train.description1') }}
                                />
                                <p
                                    dangerouslySetInnerHTML={{ __html: t('contact.transportation.train.description2') }}
                                />
                            </TransportationDescription>
                        )}
                    </TransportationOption>
                    <TransportationOption>
                        <TransportationHeader onClick={() => toggleTransportationOption(TRANSPORTATION_OPTIONS.CAR)}>
                            <TransportationHeaderTitle>
                                {t('contact.transportation.car.label')}
                            </TransportationHeaderTitle>
                            <TransportationHeaderIcon
                                className={
                                    openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.CAR)
                                        ? 'isOpen'
                                        : 'isClose'
                                }
                                name={Icons.ArrowDown}
                            />
                        </TransportationHeader>
                        {openedTransportationOptions.includes(TRANSPORTATION_OPTIONS.CAR) && (
                            <TransportationDescription>
                                <p dangerouslySetInnerHTML={{ __html: t('contact.transportation.car.description1') }} />
                            </TransportationDescription>
                        )}
                    </TransportationOption>
                </TransportationOptions>
            </Transportation>
        </ContentTemplate>
    );
};

export default AddressTemplate;
